import { useLayoutEffect } from "react";
import {
  createSharedMediaQueryStore,
  type SharedMediaQueryStore,
} from "@/shared/hooks/createSharedMediaQueryStore";
import {
  createForceDisplayModeCookies,
  DESKTOP_MIN_WIDTH_PX,
  type ForceDisplayMode,
  type ForcePcCookieAction,
  type ForcePcPreference,
  resolveAutoDesktopMode,
  resolveDesktopDisplayMode,
  resolveForcePcPreference,
  stripPcQuery,
} from "@/shared/lib/forcePc";

export {
  DESKTOP_MIN_WIDTH_PX,
  resolveAutoDesktopMode,
} from "@/shared/lib/forcePc";

let cachedForcePcPreference: ForcePcPreference | undefined;
let appliedCookieAction: ForcePcCookieAction = "none";

// pointer特性/viewport判定は引用行ごと(QuoteHoverPreview)やレス数分マウントされる
// コンポーネントからも参照されるため、matchMediaリスナーを購読者ごとに倍増させず
// アプリ全体で1本に集約する共有シングルトンストアで購読する(Issue #16 レビュー指摘、
// useSystemPrefersDark と同じ方式)。
const pointerIsFineStore: SharedMediaQueryStore =
  createSharedMediaQueryStore("(pointer: fine)");
const viewportIsDesktopStore: SharedMediaQueryStore =
  createSharedMediaQueryStore(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`);

export function useHasFinePointer(): boolean {
  return pointerIsFineStore.useMatches();
}

/** 現在のビューポート幅がPC版レイアウトを選ぶ幅(>=768px)に達しているかを返す。 */
export function useIsViewportDesktop(): boolean {
  return viewportIsDesktopStore.useMatches();
}

export function useIsDesktop(): boolean {
  const viewportIsDesktop = useIsViewportDesktop();
  const forceMode = useForceDisplayMode();

  const autoIsDesktop = resolveAutoDesktopMode(viewportIsDesktop);
  const displayMode = resolveDesktopDisplayMode(autoIsDesktop, forceMode);
  return (
    displayMode === "responsive-desktop" || displayMode === "forced-desktop"
  );
}

/**
 * 表示モードの明示指定(`"auto"` / `"pc"` / `"mobile"`)を返す。
 * URLクエリ`pc=1`/`pc=0`またはCookie(`force_pc`/`force_mobile`)から解決し、
 * URLクエリによる指定は初回描画直後にCookieへ同期する
 * (即時reloadでも設定を失わないため)。
 *
 * URLクエリ`pc`はワンショット扱いとする。Cookieへ同期した直後に
 * history.replaceStateでURLから`pc`を除去し、以後のリロード
 * (pc=付きURLのブックマーク/設定画面のreload等)で再解決されないようにする。
 * これにより、pc=付きURLがCookie側の設定(`force_mobile`含む)を毎回無断で
 * 上書きし続ける問題を防ぐ(レガシーリンクpc=1の初回機能は維持する)。
 */
export function useForceDisplayMode(): ForceDisplayMode {
  const preference = getInitialForcePcPreference();

  useLayoutEffect(() => {
    if (
      preference.cookieAction === "none" ||
      preference.cookieAction === appliedCookieAction
    ) {
      return;
    }

    for (const cookie of createForceDisplayModeCookies(
      preference.cookieAction,
    )) {
      // biome-ignore lint/suspicious/noDocumentCookie: 初回描画直後に互換cookieを同期反映し、即時reloadでも設定を失わないため
      document.cookie = cookie;
    }
    appliedCookieAction = preference.cookieAction;

    // URLクエリ`pc`はCookieへ反映済みのためワンショット化する。
    // cookieActionが"none"以外になるのはURLに`pc`が存在した場合のみ。
    const strippedUrl = stripPcQuery({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });
    window.history.replaceState(window.history.state, "", strippedUrl);
  }, [preference]);

  return preference.mode;
}

/** 現在の表示が幅ではなく利用者指定でPC版に固定されているかを返す。 */
export function useIsPcForced(): boolean {
  return useForceDisplayMode() === "pc";
}

function getInitialForcePcPreference(): ForcePcPreference {
  if (cachedForcePcPreference) return cachedForcePcPreference;
  if (typeof window === "undefined") {
    return { mode: "auto", cookieAction: "none" };
  }

  cachedForcePcPreference = resolveForcePcPreference(
    window.location.search,
    document.cookie,
  );
  return cachedForcePcPreference;
}
